// ============================================================
// PHOTO STORAGE UTILITIES
//
// Single source of truth for how the app stores and displays profile
// photos (voters and family members). Two design rules:
//
// 1. Photos live in Supabase Storage in a private 'photos' bucket.
//    The DB columns (voters.photo_url, family_members.photo_url) store
//    the storage PATH only (e.g. "voters/123.jpg"). Display sites resolve
//    the path to a short-lived signed URL on demand.
//
// 2. Backward compatibility: legacy photos saved as base64 data URIs
//    ("data:image/...;base64,...") still render. Detection is by prefix.
//    No migration needed — old uploads age out as users replace them.
//
// Files are resized client-side before upload to keep them small (~100KB).
// ============================================================

const BUCKET = 'photos';

// Resize an image File to fit within MAX×MAX pixels, encoded as JPEG.
// Returns a Blob ready for upload.
async function resizeImage(file, maxDim = 800, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      // Scale down to fit inside maxDim × maxDim, preserving aspect ratio.
      if (width > maxDim || height > maxDim) {
        if (width >= height) {
          height = Math.round(height * (maxDim / width));
          width = maxDim;
        } else {
          width = Math.round(width * (maxDim / height));
          height = maxDim;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Image encoding failed'));
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load image. Is it a valid image file?'));
    };
    img.src = url;
  });
}

// Decide whether a stored photo_url value is a Storage path, a base64
// data URI, or empty.
export function classifyPhotoValue(value) {
  if (!value) return 'empty';
  if (typeof value !== 'string') return 'empty';
  if (value.startsWith('data:')) return 'base64';
  if (value.startsWith('http://') || value.startsWith('https://')) return 'url';
  // Anything else is treated as a Storage path. We constrain Storage paths
  // to the 'voters/' and 'family_members/' prefixes when writing, but the
  // classifier accepts any non-empty non-data non-url string as a path.
  return 'path';
}

// Upload a File to Supabase Storage and return the path that should be
// written to the DB column. `kind` is 'voters' or 'family_members'.
// `id` is the row id (used to make the filename deterministic so updates
// overwrite the previous file instead of leaving orphans).
export async function uploadPhoto(supabase, file, kind, id) {
  if (!file) throw new Error('No file provided');
  if (!['voters', 'family_members'].includes(kind)) {
    throw new Error(`Invalid kind: ${kind}`);
  }
  if (!id) throw new Error('id is required');

  // Resize first. If resize fails, abort with a clear error.
  let blob;
  try {
    blob = await resizeImage(file);
  } catch (e) {
    throw new Error(`Could not process image: ${e.message}`);
  }

  // Filename is "<kind>/<id>.jpg" — deterministic so re-upload overwrites
  // the previous file. Cache-busting is handled at display time by the
  // signed-URL expiry, so even if the underlying file changes, the next
  // signed URL generated will point at the new bytes.
  const path = `${kind}/${id}.jpg`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, {
      contentType: 'image/jpeg',
      upsert: true, // overwrite previous photo for this id
    });

  if (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }

  return path;
}

// Resolve a stored photo_url value to something that can go in <img src>
// or <image href>. Handles the three formats: storage path, base64, url.
// For storage paths, generates a signed URL valid for `expiresIn` seconds.
// Returns null if the value is empty or resolution fails.
export async function getDisplayUrl(supabase, value, expiresIn = 3600) {
  const kind = classifyPhotoValue(value);
  if (kind === 'empty') return null;
  if (kind === 'base64' || kind === 'url') return value;
  // kind === 'path'
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(value, expiresIn);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
}

// Batch version: takes an array of stored photo_url values, returns a Map
// from value → display URL. Resolves all storage paths in a single
// round trip via createSignedUrls. Base64/url values pass through.
export async function getDisplayUrls(supabase, values, expiresIn = 3600) {
  const result = new Map();
  if (!values || values.length === 0) return result;

  const paths = [];
  for (const v of values) {
    const kind = classifyPhotoValue(v);
    if (kind === 'empty') continue;
    if (kind === 'base64' || kind === 'url') {
      result.set(v, v);
      continue;
    }
    paths.push(v);
  }

  if (paths.length > 0) {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(paths, expiresIn);
      if (!error && Array.isArray(data)) {
        data.forEach((entry) => {
          if (entry.path && entry.signedUrl) {
            result.set(entry.path, entry.signedUrl);
          }
        });
      }
    } catch {
      // Swallow — display sites fall back to initials if a URL is missing.
    }
  }

  return result;
}

// Delete a photo from storage. Best-effort; failures are silent because
// orphaned files are harmless.
export async function deletePhoto(supabase, value) {
  if (classifyPhotoValue(value) !== 'path') return;
  try {
    await supabase.storage.from(BUCKET).remove([value]);
  } catch {
    // Ignore.
  }
}
